import type { VisualizationEvent } from '../../types/simulationPlayback';
import {
  findCheckpointForTime,
  firstEventIndexAfterSequence,
  type ReplayPlan,
} from './buildReplayPlan';
import {
  FoupOwnershipLedger,
  type FoupOwnershipLedgerState,
  type FoupOwnershipRecord,
} from './foupOwnershipLedger';

export type OhtReplaySnapshot = {
  ohtId: string;
  loadedFoupId: string | null;
  activeOperationIds: string[];
  activeEventIds: string[];
  holdReason?: string;
  atPortId?: string;
  atJunctionId?: string;
  phase?: string;
  status?: string;
  activeTransfer?: ActiveTransferSnapshot;
};

export type ActiveTransferSnapshot = {
  kind: VisualizationEvent['kind'];
  phase: VisualizationEvent['phase'];
  action?: VisualizationEvent['action'];
  progress01: number;
  /** Progress within the currently active physical phase. */
  phaseProgress01: number;
  /** Wall-clock length of the operation `progress01` is measured against. */
  durationS?: number;
  operationId: string;
  eventId: string;
  requestId?: string;
  ohtId?: string;
  portId?: string;
  stationId?: string;
  /** World-space rail node used as the equipment handoff point. */
  handoffX?: number;
  handoffY?: number;
  /** Simulation time after which a DROP no longer holds the OHT. */
  vehicleFreeSimTimeS?: number;
  /** DROP handover progress, independent of the longer FOUP-side hold. */
  vehicleProgress01?: number;
  vehicleDurationSimS?: number;
  preSettled?: boolean;
  slotId?: string;
  foupId?: string;
  delta?: number;
};

export type PortReplaySnapshot = {
  portId: string;
  inventory: number;
  occupiedFoupIds: string[];
  activeOperationIds: string[];
  shutterOpen: boolean;
  status?: string;
  activeTransfer?: ActiveTransferSnapshot;
};

export type StockerReplaySnapshot = {
  stationId: string;
  inventory: number;
  activeOperationIds: string[];
  status?: string;
  activeTransfer?: ActiveTransferSnapshot;
  crane?: {
    phase: string;
    action?: VisualizationEvent['action'];
    foupId?: string;
    from?: string;
    to?: string;
    operationId: string;
    progress01: number;
    kind: VisualizationEvent['kind'];
  };
};

export type EquipmentReplaySnapshot = {
  stationId: string;
  inventory: number;
  processActive: boolean;
  status?: string;
  shutterByPortId: Record<string, boolean>;
  activeOperationIds: string[];
  activeTransfer?: ActiveTransferSnapshot;
};

export type JunctionReplaySnapshot = {
  junctionId: string;
  phase: 'idle' | 'enter' | 'align' | 'rotate' | 'clear';
  ohtId?: string;
  fromSegmentId?: string;
  toSegmentId?: string;
  operationId?: string;
  progress01: number;
};

export type ReplayFrameSnapshot = {
  simTime: number;
  appliedThroughSequence: number;
  foups: Record<string, FoupOwnershipRecord>;
  ohts: Record<string, OhtReplaySnapshot>;
  ports: Record<string, PortReplaySnapshot>;
  stockers: Record<string, StockerReplaySnapshot>;
  equipment: Record<string, EquipmentReplaySnapshot>;
  junctions: Record<string, JunctionReplaySnapshot>;
};

function isActiveSpan(event: VisualizationEvent, simTime: number): boolean {
  const endT = event.endT;
  if (endT === undefined) {
    // Instantaneous: active only exactly at t for visual phase cues.
    return event.t === simTime;
  }
  return event.t <= simTime && simTime < endT;
}

function junctionPhaseFromEvent(
  event: VisualizationEvent
): JunctionReplaySnapshot['phase'] {
  switch (event.phase) {
    case 'junction_enter':
      return 'enter';
    case 'junction_align':
      return 'align';
    case 'junction_rotate':
      return 'rotate';
    case 'junction_clear':
      return 'clear';
    default:
      return 'idle';
  }
}

function spanProgress01(event: VisualizationEvent, simTime: number): number {
  if (event.endT === undefined || event.endT <= event.t) return 1;
  if (simTime <= event.t) return 0;
  if (simTime >= event.endT) return 1;
  return (simTime - event.t) / (event.endT - event.t);
}

/**
 * Deterministic replay coordinator.
 * Advances ordered events once; rewinds via checkpoint restore + rebuild.
 */
export class ReplayCoordinator {
  private readonly plan: ReplayPlan;
  private simTime = 0;
  private appliedThroughSequence = Number.NEGATIVE_INFINITY;
  private nextEventIndex = 0;
  private ledger: FoupOwnershipLedger;
  private snapshot: ReplayFrameSnapshot;

  constructor(plan: ReplayPlan) {
    this.plan = plan;
    this.ledger = FoupOwnershipLedger.fromState(plan.checkpoints[0]!.ledger);
    this.snapshot = this.buildSnapshot();
  }

  getPlan(): ReplayPlan {
    return this.plan;
  }

  getSimTime(): number {
    return this.simTime;
  }

  getAppliedThroughSequence(): number {
    return this.appliedThroughSequence;
  }

  getSnapshot(): ReplayFrameSnapshot {
    return this.snapshot;
  }

  getLedgerState(): FoupOwnershipLedgerState {
    return this.ledger.getState();
  }

  /** Advance or seek to an absolute simulation time. */
  seek(simTime: number): ReplayFrameSnapshot {
    const target = Math.max(0, Math.min(this.plan.durationS, simTime));
    if (target < this.simTime || this.needsRebuild(target)) {
      this.restoreCheckpoint(target);
    }
    this.applyThroughTime(target);
    this.simTime = target;
    this.snapshot = this.buildSnapshot();
    return this.snapshot;
  }

  /** Monotonic forward step (no rewind). */
  advanceTo(simTime: number): ReplayFrameSnapshot {
    const target = Math.max(this.simTime, Math.min(this.plan.durationS, simTime));
    this.applyThroughTime(target);
    this.simTime = target;
    this.snapshot = this.buildSnapshot();
    return this.snapshot;
  }

  private needsRebuild(target: number): boolean {
    // Same time but earlier sequence window is not expressible; time seek is enough.
    // Rebuild when rewinding or when next index drifted past the target window.
    if (target < this.simTime) return true;
    if (this.nextEventIndex > this.plan.events.length) return true;
    return false;
  }

  private restoreCheckpoint(target: number): void {
    const checkpoint = findCheckpointForTime(this.plan, target);
    this.ledger = FoupOwnershipLedger.fromState(checkpoint.ledger);
    this.appliedThroughSequence = checkpoint.appliedThroughSequence;
    this.nextEventIndex =
      checkpoint.eventIndex < 0
        ? 0
        : firstEventIndexAfterSequence(
            this.plan.events,
            checkpoint.appliedThroughSequence
          );
  }

  private applyThroughTime(target: number): void {
    const { events } = this.plan;
    while (this.nextEventIndex < events.length) {
      const event = events[this.nextEventIndex]!;
      if (event.t > target) break;
      // Preserve sequence order for same-timestamp events: apply all with t <= target
      // in array/sequence order.
      this.ledger.apply(event);
      this.appliedThroughSequence = event.sequence;
      this.nextEventIndex += 1;
    }
  }

  /** Simulated-clock span covering every physical phase of one operation. */
  private operationWindow(
    event: VisualizationEvent
  ): { startT: number; endT: number } | null {
    const operation = this.plan.indexes.byOperationId[event.operationId];
    if (!operation || operation.length === 0) return null;
    let startT = Number.POSITIVE_INFINITY;
    let endT = Number.NEGATIVE_INFINITY;
    for (const item of operation) {
      if (item.intent === 'status' || item.intent === 'informational') continue;
      startT = Math.min(startT, item.t);
      endT = Math.max(endT, item.endT ?? item.t);
    }
    if (!Number.isFinite(startT) || !Number.isFinite(endT) || endT <= startT) {
      return null;
    }
    return { startT, endT };
  }

  /** Progress across the complete request-scoped physical operation. */
  private operationProgress01(event: VisualizationEvent, simTime: number): number {
    const window = this.operationWindow(event);
    if (!window) return spanProgress01(event, simTime);
    return Math.min(
      1,
      Math.max(0, (simTime - window.startT) / (window.endT - window.startT))
    );
  }

  /**
   * Seconds the operation occupies. Renderers need this to express fixed-time
   * beats (such as the hoist settle pause) inside a normalised progress track.
   */
  private operationDurationS(event: VisualizationEvent): number | undefined {
    // Stream transfer events provide a wall duration specifically for visual
    // choreography. Progress remains simulation-time ordered, while renderers
    // receive real seconds for hoist/shutter timing.
    if (typeof event.holdWallSec === 'number' && event.holdWallSec > 0) {
      return event.holdWallSec;
    }
    const window = this.operationWindow(event);
    return window ? window.endT - window.startT : undefined;
  }

  private buildSnapshot(): ReplayFrameSnapshot {
    const ledger = this.ledger.getState();
    const simTime = this.simTime;

    const ohts: Record<string, OhtReplaySnapshot> = {};
    const ports: Record<string, PortReplaySnapshot> = {};
    const stockers: Record<string, StockerReplaySnapshot> = {};
    const equipment: Record<string, EquipmentReplaySnapshot> = {};
    const junctions: Record<string, JunctionReplaySnapshot> = {};

    for (const [ohtId, foupId] of Object.entries(ledger.ohtLoadedFoupId)) {
      ohts[ohtId] = {
        ohtId,
        loadedFoupId: foupId,
        activeOperationIds: [],
        activeEventIds: [],
      };
    }

    for (const ohtId of Object.keys(this.plan.indexes.byOhtId)) {
      if (!ohts[ohtId]) {
        ohts[ohtId] = {
          ohtId,
          loadedFoupId: ledger.ohtLoadedFoupId[ohtId] ?? null,
          activeOperationIds: [],
          activeEventIds: [],
        };
      }
    }

    for (const [portId, record] of Object.entries(ledger.ports)) {
      ports[portId] = {
        portId,
        inventory: record.count,
        occupiedFoupIds: [...record.foupIds],
        activeOperationIds: [],
        shutterOpen: false,
      };
    }

    for (const portId of Object.keys(this.plan.indexes.byPortId)) {
      if (!ports[portId]) {
        ports[portId] = {
          portId,
          inventory: 0,
          occupiedFoupIds: [],
          activeOperationIds: [],
          shutterOpen: false,
        };
      }
    }

    for (const stationId of Object.keys(this.plan.indexes.byStationId)) {
      stockers[stationId] = {
        stationId,
        inventory: 0,
        activeOperationIds: [],
      };
      equipment[stationId] = {
        stationId,
        inventory: 0,
        processActive: false,
        shutterByPortId: {},
        activeOperationIds: [],
      };
    }

    for (const junctionId of Object.keys(this.plan.indexes.byJunctionId)) {
      junctions[junctionId] = {
        junctionId,
        phase: 'idle',
        progress01: 0,
      };
    }

    // Active spans / phase cues at simTime (does not re-apply ownership).
    for (const event of this.plan.events) {
      if (event.t > simTime) break;
      if (event.kind === 'STATUS_CHANGE' && event.status) {
        if (event.ohtId) {
          const oht = ohts[event.ohtId] ?? {
            ohtId: event.ohtId,
            loadedFoupId: ledger.ohtLoadedFoupId[event.ohtId] ?? null,
            activeOperationIds: [],
            activeEventIds: [],
          };
          oht.status = event.status;
          ohts[event.ohtId] = oht;
        }
        if (event.portId) {
          const port = ports[event.portId] ?? {
            portId: event.portId,
            inventory: 0,
            occupiedFoupIds: [],
            activeOperationIds: [],
            shutterOpen: false,
          };
          port.status = event.status;
          ports[event.portId] = port;
        }
        if (event.stationId) {
          if (stockers[event.stationId]) stockers[event.stationId]!.status = event.status;
          if (equipment[event.stationId]) equipment[event.stationId]!.status = event.status;
        }
      }
      if (
        event.portId &&
        (event.phase === 'shutter_open' ||
          event.phase === 'open' ||
          event.phase === 'shutter_close')
      ) {
        const isOpen = event.phase !== 'shutter_close';
        const port = ports[event.portId] ?? {
          portId: event.portId,
          inventory: 0,
          occupiedFoupIds: [],
          activeOperationIds: [],
          shutterOpen: false,
        };
        port.shutterOpen = isOpen;
        ports[event.portId] = port;
        if (event.stationId && equipment[event.stationId]) {
          equipment[event.stationId]!.shutterByPortId[event.portId] = isOpen;
        }
      }
      if (!isActiveSpan(event, simTime) && event.endT !== undefined) continue;
      if (event.endT === undefined && event.t !== simTime) {
        // Instantaneous cues only on the exact frame; ownership already applied.
        // Still allow junction/status idle defaults above.
        if (
          event.kind !== 'JUNCTION' &&
          event.kind !== 'SWITCH' &&
          event.phase !== 'shutter_open' &&
          event.phase !== 'shutter_close'
        ) {
          continue;
        }
        if (event.t !== simTime) continue;
      }

      if (!isActiveSpan(event, simTime)) continue;

      if (event.ohtId) {
        const oht = ohts[event.ohtId] ?? {
          ohtId: event.ohtId,
          loadedFoupId: ledger.ohtLoadedFoupId[event.ohtId] ?? null,
          activeOperationIds: [],
          activeEventIds: [],
        };
        ohts[event.ohtId] = oht;
        if (!oht.activeOperationIds.includes(event.operationId)) {
          oht.activeOperationIds.push(event.operationId);
        }
        if (!oht.activeEventIds.includes(event.eventId)) {
          oht.activeEventIds.push(event.eventId);
        }
        oht.phase = event.phase;
        if (event.portId) oht.atPortId = event.portId;
        if (event.junctionId) oht.atJunctionId = event.junctionId;
        if (event.kind === 'WAIT' || event.phase === 'wait') {
          oht.holdReason = event.reason ?? 'wait';
        }
        if (
          event.kind === 'OHT_ARRIVE' ||
          event.kind === 'OHT_PICKUP' ||
          event.kind === 'OHT_DROP' ||
          event.kind === 'STOCKER_TRANSFER' ||
          event.kind === 'EQUIPMENT_TRANSFER'
        ) {
          oht.activeTransfer = {
            kind: event.kind,
            phase: event.phase,
            action: event.action,
            progress01: this.operationProgress01(event, simTime),
            phaseProgress01: spanProgress01(event, simTime),
            durationS: this.operationDurationS(event),
            operationId: event.operationId,
            eventId: event.eventId,
            requestId: event.requestId,
            ohtId: event.ohtId,
            portId: event.portId,
            stationId: event.stationId,
            handoffX: event.handoffX,
            handoffY: event.handoffY,
            vehicleFreeSimTimeS: event.vehicleFreeSimTimeS,
            preSettled: event.preSettled,
            slotId: event.slotId,
            foupId: event.foupId,
            delta: event.delta,
          };
        }
      }

      if (event.portId) {
        const port = ports[event.portId] ?? {
          portId: event.portId,
          inventory: 0,
          occupiedFoupIds: [],
          activeOperationIds: [],
          shutterOpen: false,
        };
        ports[event.portId] = port;
        if (!port.activeOperationIds.includes(event.operationId)) {
          port.activeOperationIds.push(event.operationId);
        }
        if (event.phase === 'shutter_open' || event.phase === 'open') {
          port.shutterOpen = true;
        }
        if (event.phase === 'shutter_close') {
          port.shutterOpen = false;
        }
        port.activeTransfer = {
          kind: event.kind,
          phase: event.phase,
          action: event.action,
          progress01: this.operationProgress01(event, simTime),
          phaseProgress01: spanProgress01(event, simTime),
          durationS: this.operationDurationS(event),
          operationId: event.operationId,
          eventId: event.eventId,
          requestId: event.requestId,
          ohtId: event.ohtId,
          portId: event.portId,
          stationId: event.stationId,
          handoffX: event.handoffX,
          handoffY: event.handoffY,
          preSettled: event.preSettled,
          slotId: event.slotId,
          foupId: event.foupId,
          delta: event.delta,
        };
      }

      if (event.stationId) {
        if (
          event.kind === 'STOCKER_TRANSFER' ||
          event.kind === 'OHT_PICKUP' ||
          event.kind === 'OHT_DROP' ||
          event.kind === 'INVENTORY_COMMIT' ||
          event.kind === 'INTERNAL_TRANSFER'
        ) {
          const stocker = stockers[event.stationId] ?? {
            stationId: event.stationId,
            inventory: 0,
            activeOperationIds: [],
          };
          stockers[event.stationId] = stocker;
          if (!stocker.activeOperationIds.includes(event.operationId)) {
            stocker.activeOperationIds.push(event.operationId);
          }
          stocker.activeTransfer = {
            kind: event.kind,
            phase: event.phase,
            action: event.action,
            progress01: this.operationProgress01(event, simTime),
            phaseProgress01: spanProgress01(event, simTime),
            durationS: this.operationDurationS(event),
            operationId: event.operationId,
            eventId: event.eventId,
            requestId: event.requestId,
            ohtId: event.ohtId,
            portId: event.portId ?? event.fromPortId ?? event.toPortId,
            stationId: event.stationId,
            handoffX: event.handoffX,
            handoffY: event.handoffY,
            preSettled: event.preSettled,
            slotId: event.slotId,
            foupId: event.foupId,
            delta: event.delta,
          };
          if (event.phase === 'crane_move') {
            stocker.crane = {
              phase: event.phase,
              action: event.action,
              foupId: event.foupId,
              from: event.fromPortId ?? event.portId,
              to: event.toPortId ?? event.slotId,
              operationId: event.operationId,
              progress01: spanProgress01(event, simTime),
              kind: event.kind,
            };
          }
        }

        if (
          event.kind === 'EQUIPMENT_TRANSFER' ||
          event.kind === 'OHT_PICKUP' ||
          event.kind === 'OHT_DROP' ||
          event.kind === 'EQUIPMENT_PROCESS' ||
          event.kind === 'WAIT'
        ) {
          const tool = equipment[event.stationId] ?? {
            stationId: event.stationId,
            inventory: 0,
            processActive: false,
            shutterByPortId: {},
            activeOperationIds: [],
          };
          equipment[event.stationId] = tool;
          if (!tool.activeOperationIds.includes(event.operationId)) {
            tool.activeOperationIds.push(event.operationId);
          }
          if (event.kind === 'EQUIPMENT_PROCESS' || event.phase === 'process') {
            tool.processActive = true;
          }
          if (event.portId) {
            if (event.phase === 'shutter_open' || event.phase === 'open') {
              tool.shutterByPortId[event.portId] = true;
            }
            if (event.phase === 'shutter_close') {
              tool.shutterByPortId[event.portId] = false;
            }
          }
          if (
            event.kind === 'EQUIPMENT_TRANSFER' ||
            event.phase === 'handoff' ||
            event.phase === 'open' ||
            event.phase === 'shutter_open' ||
            event.phase === 'shutter_close'
          ) {
            tool.activeTransfer = {
              kind: event.kind,
              phase: event.phase,
              // Without the action the kit cannot tell a DROP from a PICKUP and
              // skips the whole shutter / FOUP sequence.
              action: event.action,
              progress01: this.operationProgress01(event, simTime),
              phaseProgress01: spanProgress01(event, simTime),
              durationS: this.operationDurationS(event),
              operationId: event.operationId,
              eventId: event.eventId,
              requestId: event.requestId,
              ohtId: event.ohtId,
              portId: event.portId,
              stationId: event.stationId,
              handoffX: event.handoffX,
              handoffY: event.handoffY,
              preSettled: event.preSettled,
              slotId: event.slotId,
              foupId: event.foupId,
              delta: event.delta,
            };
          }
        }
      }

      const junctionKey = event.junctionId ?? event.connectorId;
      if (junctionKey && (event.kind === 'JUNCTION' || event.kind === 'SWITCH')) {
        junctions[junctionKey] = {
          junctionId: junctionKey,
          phase: junctionPhaseFromEvent(event),
          ohtId: event.ohtId,
          fromSegmentId: event.fromSegmentId ?? event.segmentId,
          toSegmentId: event.toSegmentId,
          operationId: event.operationId,
          progress01: spanProgress01(event, simTime),
        };
      }
    }

    // Aggregate stocker/equipment inventory from owned ports when station maps exist.
    for (const record of Object.values(ledger.ports)) {
      // Leave station inventories as sum of known port counts tagged via events.
      void record;
    }

    for (const [stationId, stocker] of Object.entries(stockers)) {
      // Stocker count is station-wide: +1 on each DROP commit, −1 on each
      // PICKUP commit. Summing per-port inventories kept a leftover 1 when
      // the OHT dropped on one dock and picked up from another.
      let stationDelta = 0;
      let portSum = 0;
      const portIds = new Set<string>();
      for (const event of this.plan.indexes.byStationId[stationId] ?? []) {
        if (event.portId) portIds.add(event.portId);
        if (event.toPortId) portIds.add(event.toPortId);
        if (event.fromPortId) portIds.add(event.fromPortId);
        if (event.t > simTime) continue;
        if (
          (event.kind === 'INVENTORY_COMMIT' || event.phase === 'inventory_commit') &&
          typeof event.delta === 'number'
        ) {
          stationDelta += event.delta;
        }
      }
      for (const portId of portIds) {
        portSum += ports[portId]?.inventory ?? 0;
      }
      const isStockerStation = this.plan.indexes.byStationId[stationId]?.some(
        (event) => event.kind === 'STOCKER_TRANSFER'
      );
      stocker.inventory = Math.max(0, isStockerStation ? stationDelta : portSum);
      if (equipment[stationId]) {
        equipment[stationId]!.inventory = Math.max(0, portSum);
      }
    }

    return {
      simTime,
      appliedThroughSequence: this.appliedThroughSequence,
      foups: ledger.foups,
      ohts,
      ports,
      stockers,
      equipment,
      junctions,
    };
  }
}

export function createReplayCoordinator(plan: ReplayPlan): ReplayCoordinator {
  return new ReplayCoordinator(plan);
}
